import React, { useEffect, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { chartColors, getSizeColor, getUserRoleColor } from "../../../config/constantsColor";

const BarChart = ({ label, data, firstPeriodData, secondPeriodData, sizeData, userRoleList }) => {
  const getCategories = () => {
    if (label === "Counterfeit" || label === "Cross Selling") {
      return data?.size_counts?.map((item) => item.size + '"');
    } else if (label === "Scan By") {
      return data?.user_roles?.map((item) => item.user_role);
    } else if (
      label === "Top 5 Counterfeit Products" ||
      label === "Top 5 Cross Selling Products"
    ) {
      return data?.top_sizes?.map((item) => item.size + '"');
    } else if (label === "Product Over Product") {
      return data?.size_summary?.map((item) => item.size);
    } else if (label === "Period Over Period") {
      return ["Previous Period", "Current Period"];
    }
    return ['1/2"', '3/4"', '1"', '1 1/4"', '1 1/2"']; // default categories for other labels
  };

  const getChartData = () => {
    if (label === "Counterfeit" || label === "Cross Selling") {
      const totalCount = data?.size_counts?.reduce(
        (sum, item) => sum + item.count,
        0
      );

      if (totalCount === 0) {
        return [
          {
            name: "No Data",
            y: 100, // Using 100 to make a full circle
            color: "#F5F5F5", // Light gray for empty state
          },
        ];
      }

      return data?.size_counts.map((item) => ({
        name: item.size + '"',
        y: item.count, // Using  count
        color: getSizeColor(item.size, sizeData),
        // color: chartColors[item.size + '"'],
      }));
    } else if (label === "Scan By") {
      const getCount = (item) =>
        item.total_count || (item.genuine_count + item.counterfeit_count) || 0;

      const totalCount = data?.user_roles?.reduce(
        (sum, item) => sum + getCount(item),
        0
      );

      if (totalCount === 0) {
        return [
          {
            name: "No Data",
            y: 100,
            color: "#F5F5F5",
          },
        ];
      }
      return data?.user_roles?.map((item) => ({
        name: item.user_role,
        y: getCount(item),
        color: getUserRoleColor(item.user_role, userRoleList),
      }));
    } else if (
      label === "Top 5 Counterfeit Products" ||
      label === "Top 5 Cross Selling Products"
    ) {
      const totalCount = data?.top_sizes?.reduce(
        (sum, item) => sum + item.total_count,
        0
      );

      if (totalCount === 0) {
        return [
          {
            name: "No Data",
            y: 100, // Using 100 to make a full circle for empty state
            color: "#F5F5F5", // Light gray for empty state
          },
        ];
      }
      return data?.top_sizes?.map((item) => ({
        name: item.size + '"',
        y: item.count, // Using count
        color: getSizeColor(item.size, sizeData),
        // color: chartColors[item.size + '"'],
      }));
    } else if (label === "Product Over Product") {
      const totalCount = data?.size_summary?.reduce(
        (sum, item) => sum + item.count,
        0
      );

      if (totalCount === 0) {
        return [
          {
            name: "No Data",
            y: 100, // Using 100 to make a full circle for empty state
          },
        ];
      }
      return data?.size_summary?.map((item) => ({
        name: item.size,
        y: item.count, // Using count
        color: getSizeColor(item.size, sizeData),
        // color: chartColors[item.size + '"'],
      }));
    } else if (label === "Period Over Period") {
      const firstPeriodCount = firstPeriodData?.size_summary?.[0]?.count || 0;
      const secondPeriodCount = secondPeriodData?.size_summary?.[0]?.count || 0;

      return [
        {
          y: firstPeriodCount,
          color: "#B41C7A", // darker pink for previous period
        },
        {
          y: secondPeriodCount,
          color: "#F385B7", // lighter pink for current period
        },
      ];
    }
  };

  const showScroll = getCategories().length > 4;

const calculateChartWidth = () => {
  const containerWidth = window.innerWidth;
  const categories = getCategories();
  const numCategories = categories.length;
  
  // Base width per category (minimum width needed for each bar + padding)
  const minWidthPerCategory = 60;
  
  // Calculate optimal width based on number of categories
  let optimalWidth = minWidthPerCategory * numCategories;

  // Special case for Product Over Product and Period Over Period
  if (label === "Product Over Product" || label === "Period Over Period") {
    return Math.min(500, containerWidth * 0.3);
  }

  // Cap maximum width based on screen size with percentage-based constraints
  if (containerWidth >= 1920) {
    optimalWidth = Math.min(450, containerWidth * 0.25);
  } else if (containerWidth >= 1280) {
    optimalWidth = Math.min(300, containerWidth * 0.3);
  } else if (containerWidth >= 900) {
    optimalWidth = Math.min(300, containerWidth * 0.35);
  } else {
    // For tablet and mobile devices (less than 900px)
    optimalWidth = Math.min(800, containerWidth * 0.9);
  }
  // Ensure minimum width
  return Math.max(280, optimalWidth);
};

  const [chartWidth, setChartWidth] = useState(calculateChartWidth());

 useEffect(() => {
    const handleResize = () => {
      setChartWidth(calculateChartWidth());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, label]);

  const options = {
    chart: {
      type: "column",
      height: 300,
      width: chartWidth,
      zoomType: "x",
    },

    title: {
      text: "",
    },
    scrollbar: {
      enabled: getCategories().length > 5,
    },
    xAxis: {
      categories: getCategories(),

      ...(showScroll ? { min: 0, max: 4 } : {}),
      labels: {
        rotation: 0, // Change to 0 for better centering
        align: "center", // Ensures label is centered under the bar
        x: 0,
        y: 20, // Adjust if needed to control vertical position
        style: {
          fontSize: "12px",
          fontWeight: "500",
        },
      },

      tickLength: 0,
    },

    yAxis: {
      min: 0,
      title: {
        text: label,
        style: {
          fontSize: "18px",
          fontWeight: "400",
          color: "#001323",
        },
      },
      labels: {
        style: {
          fontSize: "12px",
        },
      },
      gridLineWidth: 0,
    },
    tooltip: {
      pointFormat: "<b>{point.category}</b>: <b>{point.y}</b>",
    },
    plotOptions: {
      column: {
        borderRadius: 4,
        dataLabels: {
          enabled: false,
        },
      },
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: "Scans",
        data: getChartData(),
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default BarChart;
